// package.ts
import { ENGINE_LOGGING_ENABLED } from '@/utils/Constants';
import { gameController } from './GameController';

declare var TheGame: any;

let loadingStarted = false;

export interface PackageProgress {
	downloaded: number;
	total: number | null;
	state: 'idle' | 'downloading' | 'success' | 'error' | 'aborted';
}

type ProgressCallback = (progress: PackageProgress) => void;

let currentProgress: PackageProgress = {
	downloaded: 0,
	total: null,
	state: 'idle'
};

let progressCallback: ProgressCallback | null = null;
let abortController: AbortController | null = null;

/**
 * Set callback for progress updates
 */
export function setProgressCallback(callback: ProgressCallback | null): void {
	progressCallback = callback;
	if (callback) {
		callback(currentProgress);
	}
}

/**
 * Get current progress state
 */
export function getProgress(): PackageProgress {
	return { ...currentProgress };
}

/**
 * Abort current download
 */
export function abortDownload(): void {
	if (abortController) {
		abortController.abort();
		abortController = null;
	}
}

function updateProgress(progress: Partial<PackageProgress>): void {
	currentProgress = { ...currentProgress, ...progress };
	if (progressCallback) {
		progressCallback(currentProgress);
	}
}

/**
 * Get file extension from path
 */
function getFileExtension(path: string): string {
	const parts = path.split('.');
	return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Check if file is an image
 */
function isImageFile(path: string): boolean {
	const ext = getFileExtension(path);
	return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'ico', 'svg'].includes(ext);
}

/**
 * Check if file is an audio file
 */
function isAudioFile(path: string): boolean {
	const ext = getFileExtension(path);
	return ['mp3', 'wav', 'ogg', 'aac', 'm4a', 'webm'].includes(ext);
}

/**
 * Check if file is a video file
 */
function isVideoFile(path: string): boolean {
	const ext = getFileExtension(path);
	return ['mp4', 'webm', 'ogg'].includes(ext);
}

/**
 * Collect all resource paths from the game
 */
export function collectResourcePaths(): string[] {
	const paths = new Set<string>();
	
	if (!TheGame) {
		return [];
	}

	// Collect all image paths from TheGame.Images
	if (TheGame.Images && Array.isArray(TheGame.Images)) {
		for (const image of TheGame.Images) {
			if (image.TheName) {
				paths.add(image.TheName);
			}
			
			// Collect layered images
			if (image.LayeredImages && Array.isArray(image.LayeredImages)) {
				for (const layerStr of image.LayeredImages) {
					if (layerStr && typeof layerStr === 'string' && layerStr.trim() !== '') {
						const layers = layerStr.split(',');
						for (const layer of layers) {
							const trimmed = layer.trim();
							if (trimmed) {
								paths.add(trimmed);
							}
						}
					}
				}
			}
		}
	}

	// Collect background music
	if (TheGame.bgMusic && typeof TheGame.bgMusic === 'string' && TheGame.bgMusic.trim() !== '') {
		paths.add(TheGame.bgMusic);
	}

	return Array.from(paths);
}

/**
 * Preload a single resource
 */
function preloadResource(path: string): Promise<void> {
	return new Promise((resolve) => {
		if (abortController?.signal.aborted) {
			resolve();
			return;
		}

		const url = 'images/' + path;

		if (isImageFile(path)) {
			const img = new Image();
			img.onload = () => {
				if (ENGINE_LOGGING_ENABLED) {
					console.log(`Preloaded image: ${path}`);
				}
				resolve();
			};
			img.onerror = () => {
				if (ENGINE_LOGGING_ENABLED) {
					console.log(`Failed to preload image: ${path}`);
				}
				resolve(); // Continue regardless of error
			};
			img.src = url;
		} else if (isAudioFile(path)) {
			const audio = new Audio();
			audio.oncanplaythrough = () => {
				if (ENGINE_LOGGING_ENABLED) {
					console.log(`Preloaded audio: ${path}`);
				}
				resolve();
			};
			audio.onerror = () => {
				if (ENGINE_LOGGING_ENABLED) {
					console.log(`Failed to preload audio: ${path}`);
				}
				resolve();
			};
			audio.src = url;
			audio.load();
		} else if (isVideoFile(path)) {
			const video = document.createElement('video');
			video.oncanplaythrough = () => {
				if (ENGINE_LOGGING_ENABLED) {
					console.log(`Preloaded video: ${path}`);
				}
				resolve();
			};
			video.onerror = () => {
				if (ENGINE_LOGGING_ENABLED) {
					console.log(`Failed to preload video: ${path}`);
				}
				resolve();
			};
			video.src = url;
			video.load();
		} else {
			// Unknown file type - skip it
			if (ENGINE_LOGGING_ENABLED) {
				console.log(`Skipping unknown file type: ${path}`);
			}
			resolve();
		}
	});
}

/**
 * Preload all game resources
 */
export async function loadGamePackage(): Promise<void> {
	// Check if preloading is enabled
	const Settings = (window as any).Settings;
	if (Settings && Settings.enablePreloading === false) {
		if (ENGINE_LOGGING_ENABLED) {
			console.log('Preloading disabled by user setting');
		}
		return;
	}

	if (ENGINE_LOGGING_ENABLED) {
		console.log('Preloading game resources...');
	}
	if (loadingStarted) return;
	loadingStarted = true;

	abortController = new AbortController();
	
	// Wait for TheGame to be available
	let attempts = 0;
	while (!TheGame && attempts < 50) {
		await new Promise(resolve => setTimeout(resolve, 100));
		attempts++;
	}

	if (!TheGame) {
		loadingStarted = false;
		updateProgress({ state: 'error' });
		if (ENGINE_LOGGING_ENABLED) {
			console.log('TheGame not available for resource preloading');
		}
		return;
	}

	const resourcePaths = collectResourcePaths();
	const total = resourcePaths.length;

	if (total === 0) {
		loadingStarted = false;
		updateProgress({ state: 'success' });
		if (ENGINE_LOGGING_ENABLED) {
			console.log('No resources to preload');
		}
		return;
	}

	updateProgress({
		downloaded: 0,
		total: total,
		state: 'downloading'
	});

	if (ENGINE_LOGGING_ENABLED) {
		console.log(`Preloading ${total} resources...`);
	}

	try {
		// Preload resources, 2 at a time
		let downloadedCount = 0;
		
		for (let i = 0; i < resourcePaths.length; i += 2) {
			if (abortController.signal.aborted) {
				updateProgress({ state: 'aborted' });
				return;
			}

			// Process up to 2 files in parallel
			const batch = resourcePaths.slice(i, i + 2);
			
			// Preload the files (2 at a time)
			const preloadPromises = batch.map(path => preloadResource(path));
			await Promise.all(preloadPromises);
			
			downloadedCount += batch.length;
			
			updateProgress({ 
				downloaded: downloadedCount
			});
		}

		if (ENGINE_LOGGING_ENABLED) {
			console.log('Finished preloading game resources');
		}

		updateProgress({ state: 'success' });
		abortController = null;
	} catch (ex: any) {
		if (ex.name === 'AbortError') {
			updateProgress({ state: 'aborted' });
		} else {
			updateProgress({ state: 'error' });
			if (ENGINE_LOGGING_ENABLED) {
				console.log('Error preloading resources:', ex);
			}
		}
		abortController = null;
	}
}
